import React from 'react';
import clsx from 'clsx';
import { useBackgroundContext } from '../BackgroundContext/index.js';
const Title = ({ children }) => {
    const { text900Clsx } = useBackgroundContext();
    return React.createElement("h2", { className: clsx('truncate', text900Clsx) }, children);
};
const Description = ({ children }) => {
    const { text500Clsx } = useBackgroundContext();
    return (React.createElement("div", { className: clsx('mt-2 flex items-center', text500Clsx) }, children));
};
const Simple = ({ title }) => {
    return (React.createElement("div", { className: "flex-1 min-w-0 mb-5" },
        React.createElement(Title, null, title)));
};
const WithDescription = ({ title, description }) => {
    return (React.createElement("div", { className: "flex-1 min-w-0 mb-5" },
        React.createElement(Title, null, title),
        React.createElement("div", { className: "mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6" },
            React.createElement(Description, null, description))));
};
const WithAction = ({ title, action }) => {
    return (React.createElement("div", { className: "md:flex md:items-center md:justify-between mb-5" },
        React.createElement("div", { className: "flex-1 min-w-0" },
            React.createElement(Title, null, title)),
        React.createElement("div", { className: "mt-4 flex md:mt-0 md:ml-4 space-x-3" }, action)));
};
const WithDescriptionAndAction = ({ title, description, action }) => {
    const { backgroundClsx } = useBackgroundContext();
    return (React.createElement("div", { className: clsx('lg:flex lg:items-center lg:justify-between mb-5', backgroundClsx) },
        React.createElement("div", { className: "flex-1 min-w-0" },
            React.createElement(Title, null, title),
            React.createElement("div", { className: "mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6" },
                React.createElement(Description, null, description))),
        React.createElement("div", { className: "mt-5 flex lg:mt-0 lg:ml-4 space-x-3" }, action)));
};
const WithBannerImage = props => {
    const { title, backgroundImage, backgroundColor, avatar, action, description, } = props;
    let background = React.createElement("div", { className: "h-16 w-full object-cover lg:h-24" });
    if (backgroundImage) {
        if (typeof backgroundImage === 'string') {
            background = (React.createElement("img", { className: "h-32 w-full object-cover lg:h-48", src: backgroundImage, alt: "" }));
        }
        else {
            background = backgroundImage || background;
        }
    }
    return (React.createElement("div", null,
        React.createElement("div", { className: "bg-main-200", style: { background: backgroundColor } }, background),
        React.createElement("div", { className: "max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" }, avatar ? (React.createElement("div", { className: "sm:flex sm:items-end sm:space-x-5 mb-5" },
            React.createElement("div", { className: "flex -mt-12 sm:-mt-16 relative" }, typeof avatar === 'string' ? (React.createElement("img", { className: "h-24 w-24 rounded-full ring-4 ring-surface-50 sm:h-32 sm:w-32", src: avatar, alt: title })) : (React.createElement("div", { className: "h-24 w-24 flex items-center justify-center sm:h-32 sm:w-32" }, avatar))),
            React.createElement("div", { className: "sm:flex-1 sm:min-w-0 sm:flex sm:items-center sm:justify-end sm:space-x-6 sm:pb-1 mt-5" },
                React.createElement("div", { className: "min-w-0 flex-1" },
                    React.createElement("h1", { className: "text-2xl text-basicMain-900 truncate" }, title),
                    description && (React.createElement("div", { className: "mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6" },
                        React.createElement(Description, null, description)))),
                React.createElement("div", { className: "flex flex-row space-y-0 space-x-2 mt-3 sm:mt-0" }, action)))) : (React.createElement("div", { className: "my-4 sm:flex-1 sm:min-w-0 sm:flex sm:items-center sm:justify-end sm:space-x-6 sm:pb-1" },
            React.createElement("div", { className: "mb-2 sm:mb-0 min-w-0 flex-1" },
                React.createElement("h1", { className: "text-2xl text-basicMain-900 truncate" }, title)),
            React.createElement("div", { className: "flex flex-row space-y-0 space-x-2 mt-3 sm:mt-0" }, action))))));
};
const WithImage = props => {
    const { title, avatar, action, description } = props;
    return (React.createElement("div", { className: "w-full bg-surface-50 mb-5 sm:rounded-lg shadow px-6 py-6" },
        React.createElement("div", { className: "max-w-5xl m-auto" },
            React.createElement("div", { className: "sm:flex sm:items-center sm:space-x-5" },
                React.createElement("div", { className: "h-24 w-24 flex items-center justify-center" }, avatar),
                React.createElement("div", { className: "min-w-0 flex-grow" },
                    React.createElement("h1", { className: "text-2xl text-basicMain-900 truncate" }, title),
                    description && (React.createElement("div", { className: "flex-row flex-wrap truncate" },
                        React.createElement(Description, null, description)))),
                React.createElement("div", { className: "flex flex-row space-y-0 space-x-2 mt-3 sm:mt-0 flex-shrink-0" }, action)))));
};
export const PageHeader = (props) => {
    if ('backgroundImage' in props) {
        const { title, action, backgroundImage, backgroundColor, avatar, description, } = props;
        return (React.createElement(WithBannerImage, { title: title, description: description, backgroundColor: backgroundColor, backgroundImage: backgroundImage, avatar: avatar, action: action }));
    }
    if ('avatar' in props) {
        const { title, action, avatar, description } = props;
        return (React.createElement(WithImage, { title: title, description: description, avatar: avatar, action: action }));
    }
    const { title, description, action, children, padding = 'none' } = props;
    let content;
    if (!!title && !!description && !!action) {
        content = (React.createElement(WithDescriptionAndAction, { title: title, description: description, action: action }));
    }
    else if (action) {
        content = React.createElement(WithAction, { title: title, action: action });
    }
    else if (description) {
        content = React.createElement(WithDescription, { title: title, description: description });
    }
    else if (title) {
        content = React.createElement(Simple, { title: title });
    }
    else {
        content = children;
    }
    const paddingClsx = [
        padding === 'sm' && 'px-1',
        padding === 'md' && 'px-2',
        padding === 'lg' && 'px-3',
    ];
    return React.createElement("div", { className: clsx(paddingClsx) }, content);
};
//# sourceMappingURL=PageHeader.js.map